import ProForm, {
    ProFormDatePicker,
    ProFormDateTimePicker,
    ProFormText,
    ProFormSelect,
    ProFormSwitch,
    ProFormTextArea,
    ProFormCheckbox,
    ProFormDigit,
    ProFormDateRangePicker,
} from '@ant-design/pro-form'
import { Tree, Upload, Button } from 'antd'
import { formatSelectOptions } from 'utils'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ModalFormItemSlice } from 'redux/reducers'
import { UploadOutlined } from '@ant-design/icons'
import './modalFormItem.scss'
import JSZip from 'jszip'

export const ModalFormItem = ({ formStructure, formData, formInstance }) => {
    const dispatch = useDispatch()
    // 树形控件
    const checkedKeys = useSelector(state => state.ModalFormItemSlice.checkedKeys)
    const handelTreeCheck = (checkedKeys, e) => {
        dispatch(ModalFormItemSlice.actions.addCheckedKeysAction(checkedKeys))
    }

    useEffect(() => {
        if (!formData) {
            formInstance.resetFields() // 重置表单
            dispatch(ModalFormItemSlice.actions.addCheckedKeysAction([])) // 重置树形多选框
        } else {
            formInstance.setFieldsValue(formData) // 回显表单
            dispatch(ModalFormItemSlice.actions.addCheckedKeysAction(formData?.menuIds)) // 回显树形多选
        }
    }, [formData])

    const templateJSX = item => {
        switch (item.type) {
            case 'datePicker': // 日期选择器
                return (
                    <ProFormDatePicker
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        initialValue={item.initialValue} // 设置默认值
                        width='md'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'dateRangePicker': // 日期选择器
                return (
                    <ProFormDateRangePicker
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        width='md'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'dateTimePicker': // 日期 + 时间选择器
                return (
                    <ProFormDateTimePicker
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        width='md'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'text': // 文本输入
                return (
                    <ProFormText
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        width='sm'
                        disabled={item.disabled ?? false}
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'textArea': // 多行文本
                return (
                    <ProFormTextArea
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        width='lg'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'digit': // 数字输入框
                return (
                    <ProFormDigit
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        width='sm'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'switch': // 开关选择
                return (
                    <ProFormSwitch
                        name={item.name}
                        label={item.label}
                        valuePropName='checked'
                        key={item.name}
                        width='md'
                        initialValue={true} // 设置默认值
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'select': // 下拉选择框
                return (
                    <ProFormSelect
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        request={async () => {
                            const result = formatSelectOptions(item.data, item.name)
                            return result
                        }}
                        width='lg'
                        fieldProps={{
                            showSearch: true,
                            placeholder: '',
                            ...(item.fieldProps ?? {}),
                        }}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'checkbox': // 多选框
                return (
                    <ProFormCheckbox.Group
                        name={item.name}
                        label={item.label}
                        key={item.name}
                        request={() => item.data}
                        width='lg'
                        fieldProps={item.fieldProps}
                        rules={[
                            {
                                required: item.required,
                                message: '',
                            },
                        ]}
                    />
                )
            case 'treeCheck': // 树选框
                return (
                    <div key={item.name}>
                        <div className='treeCheck'>{item.label}</div>
                        <Tree
                            checkable
                            treeData={item.data}
                            fieldNames={{ title: 'label', key: 'id', children: 'children' }}
                            checkedKeys={checkedKeys}
                            onCheck={handelTreeCheck}
                            defaultExpandAll={true}
                        />
                    </div>
                )
            case 'uploadBtn': // 上传文件
                return (
                    <Upload
                        beforeUpload={file => {
                            const zip = new JSZip()
                            zip.file(file.name, file)
                                .generateAsync({
                                    type: 'blob',
                                    compression: 'DEFLATE',
                                    compressionOptions: {
                                        level: 5,
                                    },
                                })
                                .then(content => {
                                    const fileData = new File([content], `${file.name}.zip`, {
                                        type: 'application/zip',
                                    })
                                    item.setFile(fileData)
                                })
                            return false
                        }}
                        maxCount={1}
                        key='upload'
                    >
                        <Button icon={<UploadOutlined />}>Click to Upload</Button>
                    </Upload>
                )
            default:
                return <></> // 默认不产生元素
        }
    }
    return (
        <div className='modalFormItem'>
            {formStructure?.map(item => {
                if (item.type === 'group') {
                    return (
                        <ProForm.Group title={item.title} key={item.title}>
                            {item?.children?.map(itemSub => templateJSX(itemSub))}
                        </ProForm.Group>
                    )
                } else {
                    return templateJSX(item)
                }
            })}
        </div>
    )
}
